import React, {
  useEffect, useRef, useState,
} from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssistantIcon from '@mui/icons-material/Assistant';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import type { IBusinessProfile, UpdateBusinessProfileInput } from '@optimus/models';
import OptimusButton from '../../common/library/Button/Button';
import OptimusInput from '../../common/library/Input/Input';
import { OuterContainer } from '../Dashboard/DashboardOuterContainerPagesLayout';
import { TestIds } from '../../assets/testIds';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { selectMemberInformation, selectMemberSession } from '../../store/slices/onboardingSlice';
import { selectHasSeenWelcomeBanner, setWelcomeBannerSeen } from '../../store/slices/coachSlice';
import { ChatMessage, ChatRole, useAgentStream } from '../../common/customHooks/useAgentStream';
import { MarkdownRenderer } from './MarkdownRenderer';
import { SessionFeedback } from './SessionFeedback';
import { ConfidentialityModal } from './ConfidentialityModal';
import { ThinkingDots } from './ThinkingDots';
import { Avatar } from './Avatar';
import { BetaBadge } from './BetaBadge';
import { LiveCoaching } from './LiveCoaching';
import { CoachComingSoon } from './CoachComingSoon';
import { useOptimizelyFlag } from '../../common/hooks/useOptimizelyFlag';
import { AiCoachBetaAccessFlag } from '../../common/OptimizelyFlagsConstants';
import { useCustomEventTracking } from '../../common/useCustomEventTracking';
import { BusinessProfile, BusinessProfileBanner } from './BusinessProfile';
import { ChatHistory } from './ChatHistory';
import { ChatHistoryDialog } from './ChatHistoryDialog';
import { buildCoachConversationNumbers } from '../../common/utils/coachHistory';
import {
  getBusinessProfile,
  updateBusinessProfile,
} from '../../api/businessProfileAPI';
import {
  CoachFeedbackRating,
  listCoachFeedback,
  readyCoachConversationTitle,
} from '../../api/coachAPI';
import { Logger } from '../../config/logging/logger';
import { buildBusinessProfileAgentParams } from '../../common/utils/businessProfileAgentParams';
import { COACH_ROUTE } from '../../routes/namedRoutes';
import { useBusinessTradeOptions } from '../../common/customHooks/useBusinessTradeOptions';
import { getMemberInformation } from '../../store/thunks/onboardingThunk';

const logger = Logger.create('Coach');

// Translation keys for the suggested questions shown in the starter card
// before a conversation begins.
const QUICK_START_PROMPT_KEYS = [
  'coach.prompts.biggestMistake',
  'coach.prompts.holdingBack',
];

// How the member triggered a question. Reported on the send event so Heap can
// separate deliberate "Send" clicks from the keyboard shortcut and the
// suggested-prompt pills. HomeCoachBar is a question asked from the home page.
type SendSource = 'SendButton' | 'EnterKey' | 'QuickPrompt' | 'HomeCoachBar';

// Router state the home page's coach bar navigates here with (SN-1046).
export interface CoachLocationState {
  initialQuestion?: string;
}

// The tabs are addressable by URL hash so marketing and partner integrations can
// deep-link straight to one of them, e.g. /coaching#aicoach. The hash values
// double as the MUI Tabs values.
type CoachTab = 'personalcoaching' | 'aicoach' | 'businessprofile';

const PERSONAL_COACHING_TAB: CoachTab = 'personalcoaching';
const AI_COACH_TAB: CoachTab = 'aicoach';
const BUSINESS_PROFILE_TAB: CoachTab = 'businessprofile';

// Resolves the tab from the URL hash. Anything unrecognised (including no hash
// at all) falls back to AI Business Coach, the default tab.
function tabFromHash(hash: string): CoachTab {
  const tab = hash.replace('#', '').toLowerCase();
  // Keep existing marketing and partner links working after the rename.
  if (tab === PERSONAL_COACHING_TAB || tab === 'livecoaching') return PERSONAL_COACHING_TAB;
  if (tab === BUSINESS_PROFILE_TAB) return BUSINESS_PROFILE_TAB;
  return AI_COACH_TAB;
}

interface MessageRowProps {
  message: ChatMessage;
  // True for the assistant turn that is still being produced (no text yet).
  isThinking: boolean;
  statusText: string;
  // Display name shown next to the user's avatar (their first + last name).
  userName: string;
  // True while the assistant is streaming a response.
  isStreaming: boolean;
  // True if this is the last message in the list
  isLastMessage: boolean;
  // Error message to display if this is the last assistant message with no content.
  error?: string | null;
  // Conversation and agent metadata for feedback
  conversationId: string;
  agentId: string;
  // Feedback state tracking
  isFeedbackSubmitted: boolean;
  // The rating already on record for this turn, so a reopened chat draws the
  // thumb the member chose rather than an unfilled pair.
  submittedRating?: CoachFeedbackRating | null;
  onFeedbackSubmitted: (runId: string, rating: CoachFeedbackRating) => void;
}

function MessageRow({
  message,
  isThinking,
  statusText,
  userName,
  isStreaming,
  isLastMessage,
  error,
  conversationId,
  agentId,
  isFeedbackSubmitted,
  submittedRating,
  onFeedbackSubmitted,
}: MessageRowProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isUser = message.role === ChatRole.User;
  // Show error in this message if it's an empty assistant message with an error
  const showError = message.role === ChatRole.Assistant &&
    message.content.length === 0 &&
    error;
  // Recording feedback needs both ids: we key our own record on the run id,
  // and the platform's feedback API requires the message id. An answer missing
  // either (a failed turn, or a header the gateway did not send) gets no thumbs
  // rather than thumbs that would silently fail to record.
  const isAssistant = message.role === ChatRole.Assistant;
  const feedbackRunId = isAssistant ? message.runId : undefined;
  const feedbackMessageId = isAssistant ? message.assistantMessageId : undefined;
  // Show feedback for assistant messages that have content and are complete.
  // Hide feedback if this is the last message and still streaming.
  // A rating is stored against the assistantMessageId, which only arrives when
  // the response finishes. A turn that errored never got one - whether it
  // errored before any text or part-way through a reply - so a thumb there
  // would save nothing and report nothing. Gate on the ids themselves rather
  // than on the error, so no feedback bar is offered unless a rating can be
  // recorded.
  const showFeedback = !!feedbackRunId &&
    !!feedbackMessageId &&
    !(isLastMessage && isStreaming) &&
    message.content.length > 0;

  return (
    <Box
      data-testid={TestIds.coach.message}
      sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}
    >
      {/* Author header: avatar + name (+ status while thinking) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) }}>
        <Avatar
          label={isUser ? t('coach.youLabel') : 'AI'}
          color={isUser ? theme.palette.brand.accent : theme.palette.brand.primary}
        />
        <Typography
          variant="bodyMdProminent"
          sx={{
            color: theme.palette.grey[800],
          }}
        >
          {isUser ? userName : t('coach.coachName')}
        </Typography>
        {!isUser && statusText && (
          <Typography
            variant="labelSm"
            data-testid={TestIds.coach.statusIndicator}
            sx={{
              color: theme.palette.grey[500],
            }}
          >
            {statusText}
          </Typography>
        )}
      </Box>

      {/* Message body, indented to line up under the author name (avatar
          width + its gap to the name). */}
      <Box sx={{ paddingLeft: theme.spacing(5.5), maxWidth: '100%', minWidth: 0 }}>
        {isThinking ? (
          <ThinkingDots />
        ) : (
          <Box
            data-testid={TestIds.coach.messageBubble}
            sx={{
              borderRadius: theme.spacing(1),
              padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
              backgroundColor: theme.palette.grey[50],
              color: theme.palette.grey[800],
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              // The message always stays inside the bubble. The bubble itself can
              // never be wider than the chat column, so anything that refuses to
              // wrap - a long word, a pasted URL, a run of characters with no
              // spaces - would otherwise be drawn past the rounded background
              // instead of inside it. `anywhere` breaks such a run mid-token as a
              // last resort; ordinary text still wraps at spaces as before.
              overflowWrap: 'anywhere',
              // A flex child will not shrink below its content's minimum width on
              // its own, which is what lets the text escape the bubble in the
              // first place.
              minWidth: 0,
              '& > *': {
                minWidth: 0,
                maxWidth: '100%',
              },
            }}
          >
            {message.role === ChatRole.Assistant ? (
              <>
                {showError ? (
                  <Typography
                    variant="bodyMd"
                    data-testid={TestIds.coach.errorText}
                    sx={{
                      color: theme.palette.error.main,
                    }}
                  >
                    {error}
                  </Typography>
                ) : (
                  <MarkdownRenderer content={message.content} />
                )}
                {showFeedback && feedbackRunId && feedbackMessageId && (
                  <SessionFeedback
                    conversationId={conversationId}
                    runId={feedbackRunId}
                    assistantMessageId={feedbackMessageId}
                    agentId={agentId}
                    isSubmitted={isFeedbackSubmitted}
                    submittedRating={submittedRating}
                    onFeedbackSubmitted={onFeedbackSubmitted}
                  />
                )}
              </>
            ) : (
              <Typography
                variant="bodyMd"
                sx={{
                  whiteSpace: 'pre-wrap',
                }}
              >
                {message.content}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function Coach() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  /**
   * Below `md` there is no second column for the history panel, so it moves
   * behind a button that opens it in a dialog (SN-988). Same breakpoint the grid
   * below collapses at, so the panel is never rendered without a column to fill.
   */
  const isHistoryInDialog = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    hash, search, pathname, state,
  } = useLocation();
  const navigate = useNavigate();
  // The hash is the single source of truth for the selected tab, so a deep link
  // and an in-page tab click end up in exactly the same state.
  const currentTab = tabFromHash(hash);
  const { trackEngagement, trackRequest } = useCustomEventTracking();
  const {
    tradeOptions, isLoadingTradeOptions, hasTradeOptionsError, retryTradeOptions,
  } = useBusinessTradeOptions();
  // Gates the AI Business Coach chat behind the `aiCoachBetaAccess` flag. When
  // disabled (or still resolving), the tab shows the "coming soon" splash card.
  const {
    decision: betaAccessDecision,
    isDecisionReady: isBetaAccessDecisionReady,
  } = useOptimizelyFlag({ flag: AiCoachBetaAccessFlag });
  const hasBetaAccess = isBetaAccessDecisionReady && betaAccessDecision?.enabled;
  const isChatVisible = currentTab === AI_COACH_TAB && Boolean(hasBetaAccess);
  const {
    messages,
    isStreaming,
    statusText,
    error,
    sendMessage,
    conversationId,
    agentId,
    conversations,
    historyStatus,
    isLoadingConversation,
    refreshConversations,
    startNewChat,
    openConversation,
    renameConversation,
    deleteConversation,
  } = useAgentStream({ isChatVisible });
  const memberInformation = useAppSelector(selectMemberInformation);
  const { memberId } = useAppSelector(selectMemberSession);
  const { firstName, lastName } = memberInformation;
  const hasSeenWelcomeBanner = useAppSelector(selectHasSeenWelcomeBanner);
  const [input, setInput] = useState('');
  // Remembers which tab the user was on before navigating to Business Profile,
  // so "I'll finish this later" can return them to the right tab.
  const previousTab = useRef<CoachTab>(AI_COACH_TAB);
  useEffect(() => {
    if (currentTab !== BUSINESS_PROFILE_TAB) {
      previousTab.current = currentTab;
    }
  }, [currentTab]);

  const [businessProfile, setBusinessProfile] = useState<IBusinessProfile | null>(null);
  const [businessProfileStatus, setBusinessProfileStatus] =
    useState<'loading' | 'ready' | 'error'>('loading');
  const [showConfidentialityModal, setShowConfidentialityModal] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // The rating each answer already carries, keyed by its runId - the one turn id
  // that survives a conversation being reopened from history. The rating is held
  // rather than just the fact of one, so a reopened chat can draw the thumb the
  // member actually chose and not merely thank them for it.
  const [submittedFeedback, setSubmittedFeedback] =
    useState<Map<string, CoachFeedbackRating>>(new Map());

  const hasConversation = messages.length > 0;
  const showWelcomeBanner = !hasConversation && !hasSeenWelcomeBanner;
  // A chat the gateway has not stored yet: it only becomes a history entry once
  // its first turn lands, so until then it is listed as a pending "New chat".
  const isNewChat = !conversations
    .some((conversation) => conversation.conversationId === conversationId);
  const conversationNumber = buildCoachConversationNumbers(conversations).get(conversationId);
  // What this chat is called, read the same way its history row reads it: the
  // gateway's generated name once it reports one as ready, and the numbered
  // placeholder only until then. Taking the number unconditionally left the chat
  // headed "Conversation #4" while the row beside it was already named.
  const conversationTitle = conversationNumber === undefined ?
    null :
    readyCoachConversationTitle(conversations.find(
      (conversation) => conversation.conversationId === conversationId,
    )) ?? t('coach.history.conversationTitle', { number: conversationNumber });
  // The user's full name labels their messages; fall back to "You" until the
  // member information has loaded.
  const userName = [firstName, lastName].filter(Boolean).join(' ').trim() || t('coach.youLabel');
  const companyId = memberInformation.company?.id;

  const loadBusinessProfile = async () => {
    if (!companyId) return;
    setBusinessProfileStatus('loading');
    try {
      setBusinessProfile(await getBusinessProfile(companyId));
      setBusinessProfileStatus('ready');
    } catch {
      setBusinessProfile(null);
      setBusinessProfileStatus('error');
    }
  };

  useEffect(() => {
    loadBusinessProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // The rest of the app reads the company's trades from the store, so a changed trade has to be
  // pulled back in or those screens keep showing the old one.
  const refreshMemberInformation = () => {
    dispatch(getMemberInformation({ id: memberId, trackRequest }));
  };

  const handleBusinessProfileSave = async (profileUpdate: UpdateBusinessProfileInput) => {
    if (!companyId) return;
    const updatedProfile = await updateBusinessProfile(companyId, profileUpdate);
    setBusinessProfile(updatedProfile);
    if (profileUpdate.primaryTrade) refreshMemberInformation();
  };

  const navigateToTab = (tab: CoachTab) => navigate(
    `${COACH_ROUTE}${search}#${tab}`,
    { replace: true },
  );

  // Keep the latest message in view as the conversation grows.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, statusText]);

  // Reopening a past conversation brings back messages that may already have a
  // thumbs up/down, so pull the ratings this member gave in it. Both sides match
  // on the run id: the history endpoint returns it per turn and it is what the
  // stored feedback is keyed on, so an already-rated answer comes back showing
  // the "thank you" state instead of live thumbs the backend would 409.
  useEffect(() => {
    // Switching conversations quickly leaves earlier requests in flight, and they
    // can land after the one for the chat now on screen. Applying a late response
    // would show its ratings against a conversation they do not belong to, so a
    // superseded request is ignored.
    let isCurrent = true;
    if (isChatVisible && memberId && !isNewChat) {
      listCoachFeedback(conversationId)
        .then((records) => {
          if (!isCurrent) {
            return;
          }
          setSubmittedFeedback(new Map(
            records.map((record) => [record.runId, record.rating]),
          ));
        })
        .catch((feedbackError) => logger.warn(
          Logger.format('Failed to load coach feedback for conversation', feedbackError),
        ));
    }
    return () => {
      isCurrent = false;
    };
  // isNewChat is deliberately excluded: it flips to false when the list refreshes
  // after the first turn, and re-running then would clobber the local state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, isChatVisible, memberId]);

  // Mark welcome banner as seen when conversation starts
  useEffect(() => {
    if (hasConversation && !hasSeenWelcomeBanner) {
      dispatch(setWelcomeBannerSeen());
    }
  }, [hasConversation, hasSeenWelcomeBanner, dispatch]);

  const handleSend = (text: string, source: SendSource) => {
    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }
    // Report the question's length rather than its text: the composer is covered
    // by the confidentiality notice, so the content stays out of analytics.
    trackEngagement('Coaching', 'Click', 'AiCoachSendMessage', {
      source,
      questionLength: trimmed.length,
      isFirstMessage: !hasConversation,
      conversationId,
      ...(agentId && { agentId }),
    });
    setInput('');
    sendMessage(trimmed, buildBusinessProfileAgentParams(
      businessProfile,
      memberInformation.company?.subscription?.subscriptionTier,
    ));
  };

  // A question asked from the home page's coach bar opens a new chat with it.
  // It waits for the business profile so the question carries the same context
  // as one typed here, and the state is cleared so a reload does not resend it.
  const initialQuestion = (state as CoachLocationState | null)?.initialQuestion;
  const hasStartedInitialChat = useRef(false);
  // The question waiting for the new chat, with the conversation it was asked from.
  const pendingInitialQuestion =
    useRef<{ question: string; fromConversationId: string } | null>(null);
  useEffect(() => {
    const isProfilePending = Boolean(companyId) && businessProfileStatus === 'loading';
    if (!initialQuestion || hasStartedInitialChat.current || !isChatVisible ||
      !memberId || isProfilePending) {
      return;
    }
    hasStartedInitialChat.current = true;
    pendingInitialQuestion.current = {
      question: initialQuestion,
      fromConversationId: conversationId,
    };
    setSubmittedFeedback(new Map());
    startNewChat();
    navigate(`${pathname}${search}${hash}`, { replace: true, state: null });
  // The guard ref makes this run once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuestion, isChatVisible, memberId, companyId, businessProfileStatus]);

  // Sent only once the new chat is on screen, so the send event reports that chat's
  // id and a first message, not the conversation that was open before.
  useEffect(() => {
    const pending = pendingInitialQuestion.current;
    if (!pending || conversationId === pending.fromConversationId || hasConversation) {
      return;
    }
    pendingInitialQuestion.current = null;
    handleSend(pending.question, 'HomeCoachBar');
  // handleSend is recreated every render; the pending ref makes this run once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, hasConversation]);

  const handleNewChat = () => {
    setInput('');
    setSubmittedFeedback(new Map());
    startNewChat();
  };

  const handleSelectConversation = (targetConversationId: string) => {
    if (targetConversationId === conversationId) {
      return;
    }
    setInput('');
    setSubmittedFeedback(new Map());
    openConversation(targetConversationId);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSend(input, 'EnterKey');
    }
  };

  return (
    <>
      <OuterContainer
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          padding: theme.spacing(0),
          paddingTop: theme.spacing(4),
          paddingBottom: theme.spacing(4),
          [theme.breakpoints.down('md')]: {
            background: 'none',
            border: 'none',
          },
        }}
      >
        <Box
          data-testid={TestIds.coach.chatContainer}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            gap: theme.spacing(4),
          }}
        >
          {/* Page header */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
            <Typography
              variant="h12"
              data-testid={TestIds.coach.heading}
              sx={{
                color: 'rgba(0, 0, 0, 0.87)',
              }}
            >
              {t('coach.heading')}
            </Typography>
            <Typography
              variant="body2"
              data-testid={TestIds.coach.subtitle}
              sx={{
                fontSize: theme.spacing(1.75),
                lineHeight: 'normal',
                color: theme.palette.grey[600],
              }}
            >
              {t('coach.subtitle')}
            </Typography>
          </Box>

          {/* Tabs */}
          <Box>
            <Tabs
              value={currentTab}
              // Replace rather than push so switching tabs doesn't stack history
              // entries the user has to click back through to leave the page.
              // `search` is carried over so a tab click doesn't strip the query
              // string off a marketing link (UTM params and the like).
              onChange={(_, newTab: CoachTab) => navigateToTab(newTab)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.brand.primary,
                },
                '& .MuiTabs-scroller': {
                  overflowX: 'auto !important',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                },
                '& .MuiTab-root': { flexShrink: 0 },
              }}
            >
              <Tab
                value={AI_COACH_TAB}
                data-testid={TestIds.coach.aiCoachTab}
                icon={<AutoAwesomeIcon sx={{ fontSize: theme.spacing(2.5) }} />}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    AI Business Coach
                    <BetaBadge
                      label={t('coach.betaBadge')}
                      isSelected={currentTab === AI_COACH_TAB}
                    />
                  </Box>
                }
                sx={{
                  mt: theme.spacing(1),
                  minHeight: theme.spacing(6.25),
                  ...theme.typography.body2,
                  color: theme.palette.grey[600],
                  '&.Mui-selected': {
                    color: theme.palette.brand.primary,
                  },
                }}
              />
              <Tab
                value={BUSINESS_PROFILE_TAB}
                data-testid={TestIds.coach.businessProfileTab}
                icon={<BusinessIcon sx={{ fontSize: theme.spacing(2.5) }} />}
                iconPosition="start"
                label="Business Profile"
                sx={{
                  mt: theme.spacing(1),
                  minHeight: theme.spacing(6.25),
                  ...theme.typography.body2,
                  color: theme.palette.grey[600],
                  '&.Mui-selected': { color: theme.palette.brand.primary },
                }}
              />
              <Tab
                value={PERSONAL_COACHING_TAB}
                data-testid={TestIds.coach.personalCoachingTab}
                icon={<GroupsIcon sx={{ fontSize: theme.spacing(2.5) }} />}
                iconPosition="start"
                label="Personal Coaching"
                sx={{
                  mt: theme.spacing(1),
                  minHeight: theme.spacing(6.25),
                  ...theme.typography.body2,
                  color: theme.palette.grey[600],
                  '&.Mui-selected': {
                    color: theme.palette.brand.primary,
                  },
                }}
              />
            </Tabs>
            <Divider />
          </Box>

          {/* Personal Coaching Tab Content */}
          {currentTab === PERSONAL_COACHING_TAB && (
            <LiveCoaching />
          )}

          {/* AI Business Coach Tab Content: chat when the user has beta access,
              otherwise the "coming soon" splash card. */}
          {currentTab === AI_COACH_TAB && !hasBetaAccess && <CoachComingSoon />}
          {isChatVisible && (
            // Chat History sits in a fixed-width column beside the chat on
            // desktop and stacks above it once there is no room for both.
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  md: `${theme.spacing(35)} minmax(0, 1fr)`,
                },
                // Neither column stretches to the other's height: the design has
                // the history panel run past the bottom of the chat cards, and
                // the chat keep its own height with the space beside it blank.
                alignItems: 'start',
                gap: { xs: theme.spacing(2.5), md: theme.spacing(3) },
              }}
            >
              {isHistoryInDialog ? (
                <ChatHistoryDialog
                  conversations={conversations}
                  historyStatus={historyStatus}
                  activeConversationId={conversationId}
                  isNewChat={isNewChat}
                  onNewChat={handleNewChat}
                  onSelectConversation={handleSelectConversation}
                  onRetry={refreshConversations}
                  onRenameConversation={renameConversation}
                  onDeleteConversation={deleteConversation}
                />
              ) : (
                /* The panel sizes itself against the viewport rather than against
                   the chat beside it, so it is left in the flow untouched here -
                   see useCapToViewportBottom in ChatHistory.tsx. */
                <ChatHistory
                  conversations={conversations}
                  historyStatus={historyStatus}
                  activeConversationId={conversationId}
                  isNewChat={isNewChat}
                  onNewChat={handleNewChat}
                  onSelectConversation={handleSelectConversation}
                  onRetry={refreshConversations}
                  onRenameConversation={renameConversation}
                  onDeleteConversation={deleteConversation}
                />
              )}

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(4),
                minWidth: 0,
              }}>
                {/* Welcome Banner - only show when no conversation and not seen yet */}
                {showWelcomeBanner && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: theme.spacing(1.25),
                      padding: theme.spacing(2.5),
                      borderRadius: theme.spacing(1),
                      backgroundColor: theme.palette.common.white,
                      boxShadow: `0 ${theme.spacing(0.125)} ${theme.spacing(0.5)} rgba(0, 0, 0, 0.12), 0 ${theme.spacing(0.375)} ${theme.spacing(0.25)} rgba(0, 0, 0, 0.14), 0 ${theme.spacing(0.375)} ${theme.spacing(0.1875)} rgba(0, 0, 0, 0.20)`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.125) }}>
                      <AssistantIcon
                        sx={{
                          fontSize: theme.spacing(3.125),
                          color: theme.palette.brand.primary,
                        }}
                      />
                      <Typography
                        variant="headingMd"
                        sx={{
                          color: theme.palette.brand.primary,
                        }}
                      >
                        {t('coach.welcomeTitle')}
                      </Typography>
                    </Box>
                    <Typography
                      variant="bodyMd"
                      sx={{
                        color: theme.palette.grey[600],
                      }}
                    >
                      {t('coach.welcomeDescription')}
                    </Typography>
                  </Box>
                )}

                {/* Conversation card: holds the transcript, composer and footer */}
                <Box
                  data-testid={TestIds.coach.starterCard}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(2.5),
                    // Allow card to grow with content up to available space
                    flex: '0 1 auto',
                    padding: { xs: theme.spacing(2), sm: theme.spacing(2.5) },
                    borderRadius: theme.spacing(1),
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0 ${theme.spacing(0.125)} ${theme.spacing(0.5)} rgba(0, 0, 0, 0.12), 0 ${theme.spacing(0.375)} ${theme.spacing(0.25)} rgba(0, 0, 0, 0.14), 0 ${theme.spacing(0.375)} ${theme.spacing(0.1875)} rgba(0, 0, 0, 0.20)`,
                  }}
                >
                  {/* Title of the chat on screen. Placeholder numbering until the
                    gateway returns a conversation title. */}
                  {conversationTitle && (
                    <Box>
                      <Typography
                        variant="bodyLgProminent"
                        data-testid={TestIds.coach.conversationTitle}
                        sx={{ color: theme.palette.text.primary }}
                      >
                        {conversationTitle}
                      </Typography>
                      <Divider sx={{ mt: theme.spacing(2) }} />
                    </Box>
                  )}
                  {businessProfile && (
                    <BusinessProfileBanner
                      profile={businessProfile}
                      // The banner only navigates — it deliberately does not mark the profile
                      // reviewed. The check-in is dismissed by the member either changing an
                      // answer or confirming they have no changes, so the banner keeps nagging
                      // until they actually act on it.
                      onClick={() => navigateToTab(BUSINESS_PROFILE_TAB)}
                    />
                  )}
                  {isLoadingConversation && (
                    <Box
                      data-testid={TestIds.coach.historyLoading}
                      sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) }}
                    >
                      {/* Every progress indicator on this page is drawn in the
                          org's brand colour, not MUI's default primary. */}
                      <CircularProgress
                        size={20}
                        sx={{ color: theme.palette.brand.primary }}
                      />
                      <Typography variant="bodyMd" sx={{ color: theme.palette.grey[600] }}>
                        {t('coach.history.loadingConversation')}
                      </Typography>
                    </Box>
                  )}
                  {/* A conversation that failed to load has no transcript to hang
                    the error off, so it is surfaced on its own. */}
                  {!isLoadingConversation && !hasConversation && error && (
                    <Typography
                      variant="bodyMd"
                      data-testid={TestIds.coach.errorText}
                      sx={{ color: theme.palette.error.main }}
                    >
                      {error}
                    </Typography>
                  )}
                  {/* Conversation transcript (appears once a conversation has started) */}
                  {hasConversation && (
                    <Box
                      ref={scrollRef}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing(2.5),
                        // Allow the box to grow with content, making the conversation box expand
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        paddingRight: theme.spacing(1),
                        // Show scrollbar only when content exceeds this height
                        maxHeight: `calc(100vh - ${theme.spacing(56.25)})`,
                        minHeight: theme.spacing(12.5),
                      }}
                    >
                      {messages.map((message, index) => {
                        const isLast = index === messages.length - 1;
                        // The trailing assistant turn shows the animated dots until its
                        // first text arrives.
                        const isThinking = isLast &&
                        message.role === ChatRole.Assistant &&
                        message.content.length === 0 &&
                        isStreaming;

                        return (
                          <MessageRow
                          // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            message={message}
                            isThinking={isThinking}
                            statusText={statusText}
                            userName={userName}
                            isStreaming={isStreaming}
                            isLastMessage={isLast}
                            error={isLast ? error : null}
                            conversationId={conversationId}
                            agentId={agentId}
                            isFeedbackSubmitted={
                              message.runId ? submittedFeedback.has(message.runId) : false
                            }
                            submittedRating={
                              message.runId ? submittedFeedback.get(message.runId) : undefined
                            }
                            onFeedbackSubmitted={(ratedRunId, rating) => {
                              setSubmittedFeedback(
                                (prev) => new Map(prev).set(ratedRunId, rating),
                              );
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}

                  {!hasConversation && !isLoadingConversation && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                      <Typography
                        variant="bodyMdProminent"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.grey[800],
                        }}
                      >
                        {t('coach.tryAsking')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2) }}>
                        {QUICK_START_PROMPT_KEYS.map((promptKey) => {
                          const prompt = t(promptKey);
                          return (
                            <OptimusButton
                              key={promptKey}
                              data-testid={TestIds.coach.quickPrompt}
                              customVariant="secondaryGray"
                              buttonText={prompt}
                              trailingIcon={<AutoAwesomeIcon />}
                              onClick={() => handleSend(prompt, 'QuickPrompt')}
                              disabled={isStreaming}
                              size="s"
                              fullWidth={false}
                              sx={{
                                border: 'none',
                                borderRadius: theme.spacing(12.5),
                                backgroundColor: theme.palette.grey[100],
                                color: theme.palette.brand.primary,
                                letterSpacing: 'normal',
                                fontSize: { xs: theme.spacing(1.5), sm: theme.spacing(1.75) },
                                fontWeight: 500,
                                lineHeight: theme.spacing(3),
                                // The prompts wrap and grow the pill at every width
                                // rather than only on mobile: on a smaller desktop
                                // the chat column is narrow enough that a prompt
                                // needs two lines, and pinning it to one line spills
                                // the text out of the pill (SN-988 mockup #4). The
                                // pill never gets wider than the card around it.
                                whiteSpace: 'normal',
                                maxWidth: '100%',
                                height: 'auto',
                                // Keeps a prompt short enough for one line at the
                                // pill height the design draws.
                                minHeight: theme.spacing(4.5),
                                padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
                                textAlign: 'left',
                                boxShadow: `0 ${theme.spacing(0.125)} ${theme.spacing(0.625)} 0 rgba(0, 0, 0, 0.12), 0 ${theme.spacing(0.25)} ${theme.spacing(0.25)} 0 rgba(0, 0, 0, 0.14), 0 ${theme.spacing(0.375)} ${theme.spacing(0.125)} ${theme.spacing(-0.25)} rgba(0, 0, 0, 0.2)`,
                                '&:hover:not(.Mui-disabled)': {
                                  backgroundColor: theme.palette.grey[200],
                                  backgroundImage: 'none',
                                },
                                '&:active:not(.Mui-disabled)': {
                                  backgroundColor: theme.palette.brand.primary,
                                  color: theme.palette.common.white,
                                  backgroundImage: 'none',
                                  transform: 'scale(0.96)',
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      // Stack the input above the button on mobile so neither gets
                      // crushed; sit them side-by-side from the `sm` breakpoint up.
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: theme.spacing(1.5),
                      alignItems: { xs: 'stretch', sm: 'flex-end' },
                    }}
                  >
                    {/* The input asks for the full width of its own box, so it
                        needs a box that is the row less the Send button rather
                        than the whole row - otherwise there is nothing left for
                        the button and it is pushed outside the card. */}
                    <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                      <OptimusInput
                        data-testid={TestIds.coach.messageInput}
                        width="100%"
                        // OptimusInput floors itself at 320px unless a minWidth is
                        // passed. In a chat column narrower than that the input kept
                        // its floor, overflowed the box above and was drawn under the
                        // Send button. Nothing else here needs a floor: the input is
                        // whatever the row has left once Send has its width.
                        minWidth={0}
                        size="s"
                        placeholderText={hasConversation ? `${t('coach.inputPlaceholderContinue')}` : `${t('coach.inputPlaceholderNew')}`}
                        value={input}
                        onInputChange={(event) => setInput(event.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isStreaming}
                      />
                    </Box>
                    <OptimusButton
                      data-testid={TestIds.coach.sendBtn}
                      customVariant="primary"
                      buttonText={`${t('coach.send')}`}
                      fullWidth={isMobile}
                      leadingIcon={<SendIcon fontSize="small" />}
                      onClick={() => handleSend(input, 'SendButton')}
                      disabled={isStreaming || !input.trim()}
                      sx={{
                        fontSize: theme.spacing(1.875),
                        fontWeight: 500,
                        lineHeight: theme.spacing(3.25),
                        letterSpacing: theme.spacing(0.0575),
                        // The size's 154px floor is far wider than "Send" needs and
                        // is what pushes the button off the card in a narrow chat
                        // column. The label and its padding decide the width here.
                        minWidth: 'auto',
                        padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
                        // Beside the input the button keeps its own width and the
                        // input takes what is left, instead of the two fighting
                        // over the row.
                        flexShrink: 0,
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                    <Typography
                      variant="labelSm"
                      sx={{
                        color: theme.palette.grey[500],
                      }}
                    >
                      {t('coach.confidentialityIntro')}
                      {' '}
                      {/* The icon lives inside the link so both share one click target */}
                      <Link
                        component="button"
                        onClick={() => setShowConfidentialityModal(true)}
                        data-testid={TestIds.coach.confidentialityLink}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: theme.spacing(0.5),
                          verticalAlign: 'middle',
                          fontWeight: 700,
                          color: theme.palette.brand.primary,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          border: 'none',
                          background: 'none',
                          padding: 0,
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {`${t('coach.confidentialityLink')}`}
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: theme.spacing(1.875),
                            color: theme.palette.brand.primary,
                          }}
                        />
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {currentTab === BUSINESS_PROFILE_TAB &&
            businessProfileStatus === 'ready' && businessProfile && !isLoadingTradeOptions &&
            !hasTradeOptionsError && (
            <BusinessProfile
              profile={businessProfile}
              tradeOptions={tradeOptions}
              onSave={handleBusinessProfileSave}
              onFinishLater={() => navigateToTab(previousTab.current)}
              onReviewed={() => navigateToTab(AI_COACH_TAB)}
            />
          )}
          {currentTab === BUSINESS_PROFILE_TAB &&
            (businessProfileStatus === 'loading' ||
              (businessProfileStatus === 'ready' && isLoadingTradeOptions)) && (
            <Typography color="text.secondary">
              {t('coach.businessProfile.status.loading')}
            </Typography>
          )}
          {currentTab === BUSINESS_PROFILE_TAB &&
            (businessProfileStatus === 'error' ||
              (businessProfileStatus === 'ready' && hasTradeOptionsError)) && (
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}>
              <Typography color="error">
                {t('coach.businessProfile.status.loadError')}
              </Typography>
              <OptimusButton
                customVariant="primary"
                buttonText={`${t('coach.businessProfile.actions.retry')}`}
                onClick={businessProfileStatus === 'error' ?
                  loadBusinessProfile : retryTradeOptions}
                fullWidth={false}
              />
            </Box>
          )}
        </Box>
      </OuterContainer>

      {/* Confidentiality Modal */}
      <ConfidentialityModal
        open={showConfidentialityModal}
        onClose={() => setShowConfidentialityModal(false)}
      />
    </>
  );
}
